import { useState, useEffect, useRef } from "react";
import { Menu, X, Phone, ChevronDown } from "lucide-react";
import { Button } from "./ui/button";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import altumLogo from "@/assets/altum-logo.png";
import LanguageSwitcher from "./LanguageSwitcher";
import { addLanguagePrefix, type SupportedLanguage } from "@/lib/i18n";

const Navigation = () => {
  const [scrollY, setScrollY] = useState(0);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isServicesOpen, setIsServicesOpen] = useState(false);
  const [isMobileServicesOpen, setIsMobileServicesOpen] = useState(false);
  const [isDiscountsOpen, setIsDiscountsOpen] = useState(false);
  const [isMobileDiscountsOpen, setIsMobileDiscountsOpen] = useState(false);
  const [isReferralsOpen, setIsReferralsOpen] = useState(false);
  const [isMobileReferralsOpen, setIsMobileReferralsOpen] = useState(false);
  const navRef = useRef<HTMLElement>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation('common');

  // Get current language
  const currentLang = i18n.language as SupportedLanguage;

  // Navigate with language prefix
  const navigateTo = (path: string) => {
    const langPath = addLanguagePrefix(path, currentLang);
    navigate(langPath);
  };

  // Check if current path matches (accounting for language prefix)
  const isActive = (path: string) => {
    const langPath = addLanguagePrefix(path, currentLang);
    const cleanPath = location.pathname.replace(/^\/(es)(\/|$)/, '/');
    return location.pathname === langPath || cleanPath === path;
  };
  const isServicesActive = () => {
    const cleanPath = location.pathname.replace(/^\/(es)(\/|$)/, '/');
    return cleanPath.startsWith("/services");
  };
  const isDiscountsActive = () => {
    const cleanPath = location.pathname.replace(/^\/(es)(\/|$)/, '/');
    return cleanPath.startsWith("/discounts");
  };
  const isReferralsActive = () => {
    const cleanPath = location.pathname.replace(/^\/(es)(\/|$)/, '/');
    return cleanPath.startsWith("/referrals");
  };

  // Progressive shadow based on scroll
  const isScrolled = scrollY > 20;
  const shadowOpacity = Math.min(scrollY / 100, 1) * 0.15;

  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
      // Calculate scroll progress
      const totalHeight = document.documentElement.scrollHeight - window.innerHeight;
      const progress = (window.scrollY / totalHeight) * 100;
      setScrollProgress(Math.min(progress, 100));
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll(); // Initial calculation
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Measure nav height and set CSS variable for sticky elements
  useEffect(() => {
    const updateNavHeight = () => {
      if (navRef.current) {
        const height = navRef.current.getBoundingClientRect().height;
        document.documentElement.style.setProperty('--nav-height', `${height}px`);
      }
    };

    updateNavHeight();

    const resizeObserver = new ResizeObserver(updateNavHeight);
    if (navRef.current) {
      resizeObserver.observe(navRef.current);
    }

    window.addEventListener('resize', updateNavHeight);
    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', updateNavHeight);
    };
  }, []);

  const scrollToSection = (id: string) => {
    navigateTo("/");
    setTimeout(() => {
      const element = document.getElementById(id);
      if (element) {
        element.scrollIntoView({ behavior: "smooth" });
        setIsMobileMenuOpen(false);
      }
    }, 100);
  };

  return (
    <>
      {/* Mobile Menu Backdrop */}
      <div
        className={`mobile-menu-backdrop ${isMobileMenuOpen ? 'active' : ''}`}
        onClick={() => setIsMobileMenuOpen(false)}
      />

      <nav
        ref={navRef}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          isScrolled ? "bg-white/95 backdrop-blur-md" : "bg-white"
        }`}
        style={{
          boxShadow: isScrolled
            ? `0 4px 20px rgba(0, 0, 0, ${shadowOpacity})`
            : '0 1px 3px rgba(0, 0, 0, 0.05)'
        }}
      >
        {/* Scroll Progress Indicator */}
        <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-transparent">
          <div
            className="h-full bg-primary transition-all duration-150 ease-out"
            style={{ width: `${scrollProgress}%` }}
          />
        </div>
      <div className="max-w-screen-2xl mx-auto px-4 lg:px-6 py-2 lg:py-3">
        {/* Mobile & Tablet Layout */}
        <div className="flex flex-col lg:hidden">
          {/* Top row: Logo centered, hamburger on right */}
          <div className="flex items-center justify-center h-16 relative">
            <button
              onClick={() => navigateTo("/")}
              className="flex-shrink-0 cursor-pointer"
            >
              <img
                src={altumLogo}
                alt="Altum Contractors - Houston Roofing Company Logo"
                className="h-14 w-auto"
                width={118}
                height={56}
                fetchPriority="high"
              />
            </button>
            {/* Mobile Menu Button - absolute positioned right */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="absolute right-0 top-1/2 -translate-y-1/2 text-secondary p-2 rounded-lg hover:bg-accent transition-colors"
              aria-label={isMobileMenuOpen ? "Close menu" : "Open menu"}
            >
              <div className="relative w-6 h-6">
                <Menu className={`w-6 h-6 absolute transition-all duration-300 ${isMobileMenuOpen ? 'opacity-0 rotate-90' : 'opacity-100 rotate-0'}`} />
                <X className={`w-6 h-6 absolute transition-all duration-300 ${isMobileMenuOpen ? 'opacity-100 rotate-0' : 'opacity-0 -rotate-90'}`} />
              </div>
            </button>
          </div>
          {/* Language toggle - always visible below logo */}
          <div className="flex justify-center py-2">
            <LanguageSwitcher variant="toggle" />
          </div>
        </div>

        {/* Desktop Layout */}
        <div className="hidden lg:flex items-center justify-between h-24">
          {/* Logo */}
          <button
            onClick={() => navigateTo("/")}
            className="flex-shrink-0 cursor-pointer"
          >
            <img
              src={altumLogo}
              alt="Altum Contractors - Houston Roofing Company Logo"
              className="h-16 w-auto"
              width={135}
              height={64}
              fetchPriority="high"
            />
          </button>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-8">
            <button
              onClick={() => navigateTo("/")}
              className={`nav-link-animated text-charcoal hover:text-primary transition-all font-medium ${isActive("/") ? "nav-link-active text-primary" : ""}`}
            >
              {t('nav.home')}
            </button>

            <button
              onClick={() => navigateTo("/about")}
              className={`nav-link-animated text-charcoal hover:text-primary transition-all font-medium ${isActive("/about") ? "nav-link-active text-primary" : ""}`}
            >
              {t('nav.about')}
            </button>

            {/* Services Dropdown */}
            <div
              className="relative"
              onMouseEnter={() => setIsServicesOpen(true)}
              onMouseLeave={() => setIsServicesOpen(false)}
            >
              <button
                onClick={() => setIsServicesOpen(!isServicesOpen)}
                aria-expanded={isServicesOpen}
                aria-haspopup="true"
                className={`nav-link-animated flex items-center text-charcoal hover:text-primary transition-colors font-medium ${isServicesActive() ? "nav-link-active text-primary" : ""}`}
              >
                {t('nav.services')}
                <ChevronDown className={`w-4 h-4 ml-1 transition-transform duration-300 ${isServicesOpen ? 'rotate-180' : ''}`} />
              </button>

              <div
                className={`absolute top-full left-0 mt-0 pt-2 w-64 transition-all duration-300 origin-top ${
                  isServicesOpen
                    ? "opacity-100 scale-100 translate-y-0"
                    : "opacity-0 scale-95 -translate-y-2 pointer-events-none"
                }`}
              >
                <div className="bg-white shadow-xl rounded-lg py-2 z-[100] border border-border overflow-hidden">
                  <button
                    onClick={() => { navigateTo("/services/roof-repairs"); setIsServicesOpen(false); }}
                    className={`block w-full text-left px-4 py-2.5 text-charcoal hover:bg-primary/5 hover:text-primary transition-all duration-200 hover:pl-5 ${isActive("/services/roof-repairs") ? "bg-primary/10 text-primary border-l-2 border-primary" : ""}`}
                  >
                    {t('services.roofRepairs')}
                  </button>
                  <button
                    onClick={() => { navigateTo("/services/roof-replacement"); setIsServicesOpen(false); }}
                    className={`block w-full text-left px-4 py-2.5 text-charcoal hover:bg-primary/5 hover:text-primary transition-all duration-200 hover:pl-5 ${isActive("/services/roof-replacement") ? "bg-primary/10 text-primary border-l-2 border-primary" : ""}`}
                  >
                    {t('services.roofReplacement')}
                  </button>
                  <button
                    onClick={() => { navigateTo("/services/roof-maintenance"); setIsServicesOpen(false); }}
                    className={`block w-full text-left px-4 py-2.5 text-charcoal hover:bg-primary/5 hover:text-primary transition-all duration-200 hover:pl-5 ${isActive("/services/roof-maintenance") ? "bg-primary/10 text-primary border-l-2 border-primary" : ""}`}
                  >
                    {t('services.preventativeMaintenance')}
                  </button>
                  <button
                    onClick={() => { navigateTo("/services/new-construction"); setIsServicesOpen(false); }}
                    className={`block w-full text-left px-4 py-2.5 text-charcoal hover:bg-primary/5 hover:text-primary transition-all duration-200 hover:pl-5 ${isActive("/services/new-construction") ? "bg-primary/10 text-primary border-l-2 border-primary" : ""}`}
                  >
                    {t('services.newConstruction')}
                  </button>
                  <button
                    onClick={() => { navigateTo("/services/skylights-ventilation"); setIsServicesOpen(false); }}
                    className={`block w-full text-left px-4 py-2.5 text-charcoal hover:bg-primary/5 hover:text-primary transition-all duration-200 hover:pl-5 ${isActive("/services/skylights-ventilation") ? "bg-primary/10 text-primary border-l-2 border-primary" : ""}`}
                  >
                    {t('services.enhancements')}
                  </button>
                </div>
              </div>
            </div>

            {/* Discounts Dropdown */}
            <div
              className="relative"
              onMouseEnter={() => setIsDiscountsOpen(true)}
              onMouseLeave={() => setIsDiscountsOpen(false)}
            >
              <button
                onClick={() => setIsDiscountsOpen(!isDiscountsOpen)}
                aria-expanded={isDiscountsOpen}
                aria-haspopup="true"
                className={`nav-link-animated flex items-center text-charcoal hover:text-primary transition-colors font-medium ${isDiscountsActive() ? "nav-link-active text-primary" : ""}`}
              >
                {t('nav.discounts')}
                <ChevronDown className={`w-4 h-4 ml-1 transition-transform duration-300 ${isDiscountsOpen ? 'rotate-180' : ''}`} />
              </button>

              <div
                className={`absolute top-full left-0 mt-0 pt-2 w-64 transition-all duration-300 origin-top ${
                  isDiscountsOpen
                    ? "opacity-100 scale-100 translate-y-0"
                    : "opacity-0 scale-95 -translate-y-2 pointer-events-none"
                }`}
              >
                <div className="bg-white shadow-xl rounded-lg py-2 z-[100] border border-border overflow-hidden">
                  <button
                    onClick={() => { navigateTo("/discounts/senior"); setIsDiscountsOpen(false); }}
                    className={`block w-full text-left px-4 py-2.5 text-charcoal hover:bg-primary/5 hover:text-primary transition-all duration-200 hover:pl-5 ${isActive("/discounts/senior") ? "bg-primary/10 text-primary border-l-2 border-primary" : ""}`}
                  >
                    {t('discounts.senior')}
                  </button>
                  <button
                    onClick={() => { navigateTo("/discounts/first-responders"); setIsDiscountsOpen(false); }}
                    className={`block w-full text-left px-4 py-2.5 text-charcoal hover:bg-primary/5 hover:text-primary transition-all duration-200 hover:pl-5 ${isActive("/discounts/first-responders") ? "bg-primary/10 text-primary border-l-2 border-primary" : ""}`}
                  >
                    {t('discounts.firstResponders')}
                  </button>
                </div>
              </div>
            </div>

            {/* Referrals Dropdown */}
            <div
              className="relative"
              onMouseEnter={() => setIsReferralsOpen(true)}
              onMouseLeave={() => setIsReferralsOpen(false)}
            >
              <button
                onClick={() => setIsReferralsOpen(!isReferralsOpen)}
                aria-expanded={isReferralsOpen}
                aria-haspopup="true"
                className={`nav-link-animated flex items-center text-charcoal hover:text-primary transition-colors font-medium ${isReferralsActive() ? "nav-link-active text-primary" : ""}`}
              >
                {t('nav.referrals')}
                <ChevronDown className={`w-4 h-4 ml-1 transition-transform duration-300 ${isReferralsOpen ? 'rotate-180' : ''}`} />
              </button>

              <div
                className={`absolute top-full left-0 mt-0 pt-2 w-72 transition-all duration-300 origin-top ${
                  isReferralsOpen
                    ? "opacity-100 scale-100 translate-y-0"
                    : "opacity-0 scale-95 -translate-y-2 pointer-events-none"
                }`}
              >
                <div className="bg-white shadow-xl rounded-lg py-2 z-[100] border border-border overflow-hidden">
                  <div className="px-4 py-2 text-xs font-semibold text-charcoal/50 uppercase tracking-wider">{t('referrals.forHomeowners')}</div>
                  <button
                    onClick={() => { navigateTo("/referrals/neighbors"); setIsReferralsOpen(false); }}
                    className={`block w-full text-left px-4 py-2.5 text-charcoal hover:bg-primary/5 hover:text-primary transition-all duration-200 hover:pl-5 ${isActive("/referrals/neighbors") ? "bg-primary/10 text-primary border-l-2 border-primary" : ""}`}
                  >
                    {t('referrals.neighbors')}
                  </button>
                  <div className="border-t border-border my-2"></div>
                  <div className="px-4 py-2 text-xs font-semibold text-charcoal/50 uppercase tracking-wider">{t('referrals.forPartners')}</div>
                  <button
                    onClick={() => { navigateTo("/referrals/realtors"); setIsReferralsOpen(false); }}
                    className={`block w-full text-left px-4 py-2.5 text-charcoal hover:bg-primary/5 hover:text-primary transition-all duration-200 hover:pl-5 ${isActive("/referrals/realtors") ? "bg-primary/10 text-primary border-l-2 border-primary" : ""}`}
                  >
                    {t('referrals.realtors')}
                  </button>
                  <button
                    onClick={() => { navigateTo("/referrals/home-inspectors"); setIsReferralsOpen(false); }}
                    className={`block w-full text-left px-4 py-2.5 text-charcoal hover:bg-primary/5 hover:text-primary transition-all duration-200 hover:pl-5 ${isActive("/referrals/home-inspectors") ? "bg-primary/10 text-primary border-l-2 border-primary" : ""}`}
                  >
                    {t('referrals.homeInspectors')}
                  </button>
                  <button
                    onClick={() => { navigateTo("/referrals/insurance-agents"); setIsReferralsOpen(false); }}
                    className={`block w-full text-left px-4 py-2.5 text-charcoal hover:bg-primary/5 hover:text-primary transition-all duration-200 hover:pl-5 ${isActive("/referrals/insurance-agents") ? "bg-primary/10 text-primary border-l-2 border-primary" : ""}`}
                  >
                    {t('referrals.insuranceAgents')}
                  </button>
                  <button
                    onClick={() => { navigateTo("/referrals/trade-partners"); setIsReferralsOpen(false); }}
                    className={`block w-full text-left px-4 py-2.5 text-charcoal hover:bg-primary/5 hover:text-primary transition-all duration-200 hover:pl-5 ${isActive("/referrals/trade-partners") ? "bg-primary/10 text-primary border-l-2 border-primary" : ""}`}
                  >
                    {t('referrals.tradePartners')}
                  </button>
                </div>
              </div>
            </div>

            <button
              onClick={() => navigateTo("/blog")}
              className={`nav-link-animated text-charcoal hover:text-primary transition-all font-medium ${isActive("/blog") || location.pathname.includes("/blog/") ? "nav-link-active text-primary" : ""}`}
            >
              {t('nav.blog')}
            </button>

            <button
              onClick={() => navigateTo("/reviews")}
              className={`nav-link-animated text-charcoal hover:text-primary transition-all font-medium ${isActive("/reviews") ? "nav-link-active text-primary" : ""}`}
            >
              {t('nav.reviews')}
            </button>

            <Button
              variant="cta"
              onClick={() => navigateTo("/book-inspection")}
            >
              {t('nav.getFreeInspection')}
            </Button>

            {/* Phone + Language Toggle */}
            <div className="flex items-center gap-3 pl-2 border-l border-border">
              <a
                href="tel:+13462200989"
                className="flex items-center text-secondary hover:text-primary transition-all duration-300 font-semibold group"
              >
                <Phone className="w-4 h-4 mr-2 transition-transform duration-300 group-hover:scale-110 group-hover:rotate-12" />
                {t('nav.phone')}
              </a>
              <LanguageSwitcher variant="toggle" />
            </div>
          </div>

        </div>

        {/* Mobile & Tablet Menu */}
        <div
          className={`lg:hidden overflow-hidden transition-all duration-600 ease-[cubic-bezier(0.22,1,0.36,1)] ${
            isMobileMenuOpen ? 'max-h-[700px] opacity-100 pb-4' : 'max-h-0 opacity-0'
          }`}
          style={{ transitionProperty: 'max-height, opacity, padding' }}
        >
          <div className="space-y-1 pt-2">
            <button
              onClick={() => { navigateTo("/"); setIsMobileMenuOpen(false); }}
              className={`block w-full text-left px-4 py-3 rounded transition-all duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] ${
                isActive("/") ? "bg-primary/10 text-primary font-semibold border-l-2 border-primary" : "text-charcoal hover:bg-accent"
              }`}
              style={{
                opacity: isMobileMenuOpen ? 1 : 0,
                transform: isMobileMenuOpen ? 'translateX(0)' : 'translateX(-8px)',
                transition: 'opacity 0.4s cubic-bezier(0.22,1,0.36,1) 0.05s, transform 0.4s cubic-bezier(0.22,1,0.36,1) 0.05s, background-color 0.2s'
              }}
            >
              {t('nav.home')}
            </button>

            {/* About - Second position */}
            <button
              onClick={() => { navigateTo("/about"); setIsMobileMenuOpen(false); }}
              className={`block w-full text-left px-4 py-3 rounded transition-colors duration-200 ${
                isActive("/about") ? "bg-primary/10 text-primary font-semibold border-l-2 border-primary" : "text-charcoal hover:bg-accent"
              }`}
              style={{
                opacity: isMobileMenuOpen ? 1 : 0,
                transform: isMobileMenuOpen ? 'translateX(0)' : 'translateX(-8px)',
                transition: 'opacity 0.4s cubic-bezier(0.22,1,0.36,1) 0.1s, transform 0.4s cubic-bezier(0.22,1,0.36,1) 0.1s, background-color 0.2s'
              }}
            >
              {t('nav.about')}
            </button>

            {/* Services Accordion - Third position */}
            <div
              style={{ 
                opacity: isMobileMenuOpen ? 1 : 0,
                transform: isMobileMenuOpen ? 'translateX(0)' : 'translateX(-8px)',
                transition: 'opacity 0.4s cubic-bezier(0.22,1,0.36,1) 0.15s, transform 0.4s cubic-bezier(0.22,1,0.36,1) 0.15s'
              }}
            >
              <button
                onClick={() => setIsMobileServicesOpen(!isMobileServicesOpen)}
                aria-expanded={isMobileServicesOpen}
                className={`flex items-center justify-between w-full px-4 py-3 rounded transition-colors duration-200 ${
                  isServicesActive() ? "bg-primary/10 text-primary font-semibold" : "text-charcoal hover:bg-accent"
                }`}
              >
                <span>{t('nav.services')}</span>
                <ChevronDown className={`w-4 h-4 transition-transform duration-400 ease-[cubic-bezier(0.22,1,0.36,1)] ${isMobileServicesOpen ? 'rotate-180' : ''}`} />
              </button>
              <div
                className={`overflow-hidden transition-all duration-400 ease-[cubic-bezier(0.22,1,0.36,1)] ${
                  isMobileServicesOpen ? 'max-h-[350px] opacity-100' : 'max-h-0 opacity-0'
                }`}
              >
                <div className="space-y-1 pl-4 pr-4 py-2 bg-accent/50 rounded-lg mx-4">
                  <button
                    onClick={() => { navigateTo("/services/roof-repairs"); setIsMobileMenuOpen(false); }}
                    className={`block w-full text-left py-2.5 px-3 rounded transition-all duration-200 ${
                      isActive("/services/roof-repairs") ? "bg-white text-primary font-medium" : "text-charcoal hover:text-primary hover:bg-white/50"
                    }`}
                  >
                    {t('services.roofRepairs')}
                  </button>
                  <button
                    onClick={() => { navigateTo("/services/roof-replacement"); setIsMobileMenuOpen(false); }}
                    className={`block w-full text-left py-2.5 px-3 rounded transition-all duration-200 ${
                      isActive("/services/roof-replacement") ? "bg-white text-primary font-medium" : "text-charcoal hover:text-primary hover:bg-white/50"
                    }`}
                  >
                    {t('services.roofReplacement')}
                  </button>
                  <button
                    onClick={() => { navigateTo("/services/roof-maintenance"); setIsMobileMenuOpen(false); }}
                    className={`block w-full text-left py-2.5 px-3 rounded transition-all duration-200 ${
                      isActive("/services/roof-maintenance") ? "bg-white text-primary font-medium" : "text-charcoal hover:text-primary hover:bg-white/50"
                    }`}
                  >
                    {t('services.preventativeMaintenance')}
                  </button>
                  <button
                    onClick={() => { navigateTo("/services/new-construction"); setIsMobileMenuOpen(false); }}
                    className={`block w-full text-left py-2.5 px-3 rounded transition-all duration-200 ${
                      isActive("/services/new-construction") ? "bg-white text-primary font-medium" : "text-charcoal hover:text-primary hover:bg-white/50"
                    }`}
                  >
                    {t('services.newConstruction')}
                  </button>
                  <button
                    onClick={() => { navigateTo("/services/skylights-ventilation"); setIsMobileMenuOpen(false); }}
                    className={`block w-full text-left py-2.5 px-3 rounded transition-all duration-200 ${
                      isActive("/services/skylights-ventilation") ? "bg-white text-primary font-medium" : "text-charcoal hover:text-primary hover:bg-white/50"
                    }`}
                  >
                    {t('services.enhancements')}
                  </button>
                </div>
              </div>
            </div>

            {/* Discounts Accordion */}
            <div
              style={{
                opacity: isMobileMenuOpen ? 1 : 0,
                transform: isMobileMenuOpen ? 'translateX(0)' : 'translateX(-8px)',
                transition: 'opacity 0.4s cubic-bezier(0.22,1,0.36,1) 0.2s, transform 0.4s cubic-bezier(0.22,1,0.36,1) 0.2s'
              }}
            >
              <button
                onClick={() => setIsMobileDiscountsOpen(!isMobileDiscountsOpen)}
                aria-expanded={isMobileDiscountsOpen}
                className={`flex items-center justify-between w-full px-4 py-3 rounded transition-colors duration-200 ${
                  isDiscountsActive() ? "bg-primary/10 text-primary font-semibold" : "text-charcoal hover:bg-accent"
                }`}
              >
                <span>{t('nav.discounts')}</span>
                <ChevronDown className={`w-4 h-4 transition-transform duration-400 ease-[cubic-bezier(0.22,1,0.36,1)] ${isMobileDiscountsOpen ? 'rotate-180' : ''}`} />
              </button>
              <div
                className={`overflow-hidden transition-all duration-400 ease-[cubic-bezier(0.22,1,0.36,1)] ${
                  isMobileDiscountsOpen ? 'max-h-[200px] opacity-100' : 'max-h-0 opacity-0'
                }`}
              >
                <div className="space-y-1 pl-4 pr-4 py-2 bg-accent/50 rounded-lg mx-4">
                  <button
                    onClick={() => { navigateTo("/discounts/senior"); setIsMobileMenuOpen(false); }}
                    className={`block w-full text-left py-2.5 px-3 rounded transition-all duration-200 ${
                      isActive("/discounts/senior") ? "bg-white text-primary font-medium" : "text-charcoal hover:text-primary hover:bg-white/50"
                    }`}
                  >
                    {t('discounts.senior')}
                  </button>
                  <button
                    onClick={() => { navigateTo("/discounts/first-responders"); setIsMobileMenuOpen(false); }}
                    className={`block w-full text-left py-2.5 px-3 rounded transition-all duration-200 ${
                      isActive("/discounts/first-responders") ? "bg-white text-primary font-medium" : "text-charcoal hover:text-primary hover:bg-white/50"
                    }`}
                  >
                    {t('discounts.firstResponders')}
                  </button>
                </div>
              </div>
            </div>

            {/* Referrals Accordion */}
            <div
              style={{
                opacity: isMobileMenuOpen ? 1 : 0,
                transform: isMobileMenuOpen ? 'translateX(0)' : 'translateX(-8px)',
                transition: 'opacity 0.4s cubic-bezier(0.22,1,0.36,1) 0.22s, transform 0.4s cubic-bezier(0.22,1,0.36,1) 0.22s'
              }}
            >
              <button
                onClick={() => setIsMobileReferralsOpen(!isMobileReferralsOpen)}
                aria-expanded={isMobileReferralsOpen}
                className={`flex items-center justify-between w-full px-4 py-3 rounded transition-colors duration-200 ${
                  isReferralsActive() ? "bg-primary/10 text-primary font-semibold" : "text-charcoal hover:bg-accent"
                }`}
              >
                <span>{t('nav.referrals')}</span>
                <ChevronDown className={`w-4 h-4 transition-transform duration-400 ease-[cubic-bezier(0.22,1,0.36,1)] ${isMobileReferralsOpen ? 'rotate-180' : ''}`} />
              </button>
              <div
                className={`overflow-hidden transition-all duration-400 ease-[cubic-bezier(0.22,1,0.36,1)] ${
                  isMobileReferralsOpen ? 'max-h-[400px] opacity-100' : 'max-h-0 opacity-0'
                }`}
              >
                <div className="space-y-1 pl-4 pr-4 py-2 bg-accent/50 rounded-lg mx-4">
                  <div className="text-xs font-semibold text-charcoal/50 uppercase tracking-wider px-3 py-1">{t('referrals.forHomeowners')}</div>
                  <button
                    onClick={() => { navigateTo("/referrals/neighbors"); setIsMobileMenuOpen(false); }}
                    className={`block w-full text-left py-2.5 px-3 rounded transition-all duration-200 ${
                      isActive("/referrals/neighbors") ? "bg-white text-primary font-medium" : "text-charcoal hover:text-primary hover:bg-white/50"
                    }`}
                  >
                    {t('referrals.neighbors')}
                  </button>
                  <div className="border-t border-charcoal/10 my-2 mx-3"></div>
                  <div className="text-xs font-semibold text-charcoal/50 uppercase tracking-wider px-3 py-1">{t('referrals.forPartners')}</div>
                  <button
                    onClick={() => { navigateTo("/referrals/realtors"); setIsMobileMenuOpen(false); }}
                    className={`block w-full text-left py-2.5 px-3 rounded transition-all duration-200 ${
                      isActive("/referrals/realtors") ? "bg-white text-primary font-medium" : "text-charcoal hover:text-primary hover:bg-white/50"
                    }`}
                  >
                    {t('referrals.realtors')}
                  </button>
                  <button
                    onClick={() => { navigateTo("/referrals/home-inspectors"); setIsMobileMenuOpen(false); }}
                    className={`block w-full text-left py-2.5 px-3 rounded transition-all duration-200 ${
                      isActive("/referrals/home-inspectors") ? "bg-white text-primary font-medium" : "text-charcoal hover:text-primary hover:bg-white/50"
                    }`}
                  >
                    {t('referrals.homeInspectors')}
                  </button>
                  <button
                    onClick={() => { navigateTo("/referrals/insurance-agents"); setIsMobileMenuOpen(false); }}
                    className={`block w-full text-left py-2.5 px-3 rounded transition-all duration-200 ${
                      isActive("/referrals/insurance-agents") ? "bg-white text-primary font-medium" : "text-charcoal hover:text-primary hover:bg-white/50"
                    }`}
                  >
                    {t('referrals.insuranceAgents')}
                  </button>
                  <button
                    onClick={() => { navigateTo("/referrals/trade-partners"); setIsMobileMenuOpen(false); }}
                    className={`block w-full text-left py-2.5 px-3 rounded transition-all duration-200 ${
                      isActive("/referrals/trade-partners") ? "bg-white text-primary font-medium" : "text-charcoal hover:text-primary hover:bg-white/50"
                    }`}
                  >
                    {t('referrals.tradePartners')}
                  </button>
                </div>
              </div>
            </div>

            {/* Blog */}
            <button
              onClick={() => { navigateTo("/blog"); setIsMobileMenuOpen(false); }}
              className={`block w-full text-left px-4 py-3 rounded transition-colors duration-200 ${
                isActive("/blog") || location.pathname.includes("/blog/") ? "bg-primary/10 text-primary font-semibold border-l-2 border-primary" : "text-charcoal hover:bg-accent"
              }`}
              style={{
                opacity: isMobileMenuOpen ? 1 : 0,
                transform: isMobileMenuOpen ? 'translateX(0)' : 'translateX(-8px)',
                transition: 'opacity 0.4s cubic-bezier(0.22,1,0.36,1) 0.25s, transform 0.4s cubic-bezier(0.22,1,0.36,1) 0.25s, background-color 0.2s'
              }}
            >
              {t('nav.blog')}
            </button>

            <button
              onClick={() => { navigateTo("/reviews"); setIsMobileMenuOpen(false); }}
              className={`block w-full text-left px-4 py-3 rounded transition-colors duration-200 ${
                isActive("/reviews") ? "bg-primary/10 text-primary font-semibold border-l-2 border-primary" : "text-charcoal hover:bg-accent"
              }`}
              style={{
                opacity: isMobileMenuOpen ? 1 : 0,
                transform: isMobileMenuOpen ? 'translateX(0)' : 'translateX(-8px)',
                transition: 'opacity 0.4s cubic-bezier(0.22,1,0.36,1) 0.25s, transform 0.4s cubic-bezier(0.22,1,0.36,1) 0.25s, background-color 0.2s'
              }}
            >
              {t('nav.reviews')}
            </button>

            <div
              className="px-4 pt-2"
              style={{
                opacity: isMobileMenuOpen ? 1 : 0,
                transform: isMobileMenuOpen ? 'translateX(0)' : 'translateX(-8px)',
                transition: 'opacity 0.4s cubic-bezier(0.22,1,0.36,1) 0.25s, transform 0.4s cubic-bezier(0.22,1,0.36,1) 0.25s'
              }}
            >
              <Button
                variant="cta"
                onClick={() => { navigateTo("/book-inspection"); setIsMobileMenuOpen(false); }}
                className="w-full"
              >
                {t('nav.getFreeInspection')}
              </Button>
            </div>
            <a
              href="tel:+13462200989"
              className="flex items-center px-4 py-3 text-secondary hover:bg-accent rounded font-semibold transition-colors duration-200 group"
              style={{
                opacity: isMobileMenuOpen ? 1 : 0,
                transform: isMobileMenuOpen ? 'translateX(0)' : 'translateX(-8px)',
                transition: 'opacity 0.4s cubic-bezier(0.22,1,0.36,1) 0.3s, transform 0.4s cubic-bezier(0.22,1,0.36,1) 0.3s, background-color 0.2s'
              }}
            >
              <Phone className="w-4 h-4 mr-2 transition-transform duration-300 group-hover:scale-110" />
              {t('nav.phone')}
            </a>

          </div>
        </div>
      </div>
    </nav>
    </>
  );
};

export default Navigation;
